import { useState, useCallback, useEffect, useRef } from "react";
import Autoplay from "embla-carousel-autoplay";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Quote } from "lucide-react";
import { IconPetal, IconTrust } from "@/components/icons";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import guestlyLogoMark from "@/assets/guestly-logo-mark.svg";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";

interface ReviewRow {
  nps_score: number;
  nps_reason: string | null;
  guest_name: string;
  experience_title: string;
  experience_slug: string;
  submitted_at: string | null;
  category: string | null;
}

function useGuestHighlights() {
  return useQuery({
    queryKey: ["guest-highlights"],
    queryFn: async (): Promise<{ reviews: ReviewRow[]; totalCount: number; globalAvg: number | null }> => {
      const { data: rawData, error } = await supabase
        .from("v_public_reviews" as any)
        .select("nps_score, nps_reason, guest_name, experience_id, submitted_at")
        .not("nps_score", "is", null)
        .order("submitted_at", { ascending: false })
        .limit(50);

      const data = rawData as unknown as { nps_score: number; nps_reason: string | null; guest_name: string; experience_id: string; submitted_at: string }[] | null;

      if (error || !data || data.length === 0)
        return { reviews: [], totalCount: 0, globalAvg: null };

      const totalCount = data.length;
      const globalAvg =
        (data.reduce((sum, r) => sum + (r.nps_score ?? 0), 0) / totalCount) / 2;

      const experienceIds = [...new Set(data.map((r) => r.experience_id))];
      const { data: experiences } = await supabase
        .from("experiences")
        .select("id, title, category, slug")
        .in("id", experienceIds);

      const expMap = new Map(experiences?.map((e) => [e.id, { title: e.title, category: e.category, slug: e.slug }]) ?? []);

      const reviews = data.map((r) => ({
        nps_score: r.nps_score!,
        nps_reason: r.nps_reason,
        guest_name: r.guest_name,
        experience_title: expMap.get(r.experience_id)?.title ?? "",
        experience_slug: expMap.get(r.experience_id)?.slug ?? "",
        submitted_at: r.submitted_at,
        category: expMap.get(r.experience_id)?.category ?? null,
      }));

      return { reviews, totalCount, globalAvg };
    },
    staleTime: 5 * 60 * 1000,
  });
}

function npsToStars(nps: number): number {
  if (nps >= 9) return 5;
  if (nps >= 7) return 4;
  if (nps >= 5) return 3;
  if (nps >= 3) return 2;
  return 1;
}

function formatName(name: string) {
  const parts = name.split(" ");
  if (parts.length > 1) return `${parts[0]} ${parts[1][0]}.`;
  return parts[0];
}

function StarRating({ score }: { score: number }) {
  const stars = npsToStars(score);
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <IconPetal
          key={i}
          className={`h-4 w-4 ${
            i < stars
              ? "fill-amber-400 text-amber-400"
              : "fill-muted text-muted"
          }`}
          filled={i < stars}
        />
      ))}
    </div>
  );
}

function CarouselDots({ count, current }: { count: number; current: number }) {
  return (
    <div className="flex justify-center gap-2 mt-6">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          className={`h-2 w-2 rounded-full transition-colors ${
            i === current ? "bg-foreground" : "bg-muted-foreground/30"
          }`}
          aria-label={`Go to slide ${i + 1}`}
        />
      ))}
    </div>
  );
}

export function GuestTestimonials() {
  const { data } = useGuestHighlights();
  const { t } = useUiTranslations();
  const [api, setApi] = useState<CarouselApi>();
  const [currentSnap, setCurrentSnap] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrentSnap(api.selectedScrollSnap());
    setSnapCount(api.scrollSnapList().length);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  const reviews = data?.reviews ?? [];
  const totalCount = data?.totalCount ?? 0;
  const globalAvg = data?.globalAvg ?? null;
  const hasReviews = reviews.length > 0;

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground mb-3">
            {t("testimonials.eyebrow")}
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
            {t("testimonials.heading")}
          </h2>
          {globalAvg !== null && (
            <p className="mt-3 text-sm text-muted-foreground flex items-center justify-center gap-1.5">
              {t("testimonials.avgRating").replace("{avg}", globalAvg.toFixed(1)).replace("{count}", String(totalCount))}
            </p>
          )}
        </div>

        {hasReviews ? (
          <Carousel
            opts={{ loop: true, align: "start" }}
            plugins={[Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent>
              {reviews.map((review, i) => (
                <CarouselItem
                  key={i}
                  className="basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <div className="rounded-xl border border-border bg-card p-6 flex flex-col justify-between h-full">
                    <div>
                      <Quote className="h-5 w-5 text-muted-foreground/40 mb-3" />
                      {review.nps_reason ? (
                        <p className="text-sm leading-relaxed text-foreground italic">
                          &ldquo;{review.nps_reason}&rdquo;
                        </p>
                      ) : (
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <IconPetal
                              key={s}
                              className={`h-3.5 w-3.5 ${
                                s <= npsToStars(review.nps_score)
                                  ? "fill-amber-400 text-amber-400"
                                  : "fill-muted text-muted"
                              }`}
                              filled={s <= npsToStars(review.nps_score)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-5 pt-4 border-t border-border">
                      <StarRating score={review.nps_score} />
                      <p className="mt-2 text-sm font-medium text-foreground">
                        {formatName(review.guest_name)}
                        {review.submitted_at && (
                          <span className="font-normal text-muted-foreground">
                            {" · "}
                            {format(parseISO(review.submitted_at), "MMM yyyy")}
                          </span>
                        )}
                      </p>
                      {review.experience_title && review.experience_slug && (
                        <LocalizedLink
                          to={`/experiences/${(review as any).experience_city_slug ?? "unknown"}/${review.experience_slug}`}
                          className="text-xs text-muted-foreground hover:text-primary hover:underline underline-offset-2 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {review.experience_title}
                        </LocalizedLink>
                      )}
                      {review.category && (
                        <Badge variant="secondary" className="mt-2 text-[10px] px-2 py-0.5">
                          {review.category}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-4 pt-3 border-t border-border/50 flex items-center gap-1.5">
                      <IconTrust className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-[10px] text-muted-foreground">
                        {t("testimonials.verified")}
                      </span>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 md:-left-12" />
            <CarouselNext className="-right-4 md:-right-12" />
            {snapCount > 1 && (
              <CarouselDots count={snapCount} current={currentSnap} />
            )}
          </Carousel>
        ) : (
          <div className="grid gap-8 sm:grid-cols-3 text-center">
            {[
              { stat: "100%", label: t("testimonials.qualityChecked") },
              { stat: t("card.freeCancellation"), label: t("testimonials.cancellationAlways") },
              { stat: "Local", label: t("testimonials.localGuides") },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-3xl font-semibold text-foreground">
                  {item.stat}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Trust badge */}
        <div className="mt-12 flex items-center justify-center gap-4">
          <img
            src={guestlyLogoMark}
            alt="Guestly quality seal"
            className="h-10 w-10 object-contain"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(72%) sepia(30%) saturate(600%) hue-rotate(5deg) brightness(95%) contrast(90%)",
            }}
          />
          <p className="text-xs font-medium text-foreground/70">
            {t("testimonials.trustBadge")}
          </p>
        </div>
      </div>
    </section>
  );
}
